import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Volume2, VolumeX } from 'lucide-react'
import { useFetchAgreementByRead } from '@/lib/hooks/agreement'

import { useParams } from 'react-router-dom'
import { getCleanDomain } from '@/utils/getCleanDomain'
import { generatePDF } from '@/features/agreement/utils/pdf-util'
import FullPageSpinner from '@/components/ui/full-page-sinner'
import { TypeContrato } from '@/features/agreement/utils/enum'
import { getDomainIcon } from '@/features/agreement/utils/domainIcon'

export default function MinutaRead() {
  const [isSpeaking, setIsSpeaking] = useState(false) // Controla o estado de leitura

  // Fetch agreement data

  const { idtenant, contratos } = useParams<{
    idtenant: string
    contratos: string
  }>()
  const contractArray: TypeContrato[] = contratos ? (contratos.split(',') as TypeContrato[]) : []

  // 3. Buscar dados do contrato usando o hook
  const { data, error, isLoading } = idtenant
    ? useFetchAgreementByRead(idtenant, contractArray)
    : { data: null, error: null, isLoading: false }

  const htmlContent = typeof data === 'string' ? data : ''

  const fullStyledHtml = htmlContent
    .replace(/\{\{[\s\S]*?\}\}/g, '')
    .replace(/<p>\s*(\d+º:|[A-ZÀ-Ÿ_]+)\b/g, '<p class="spaced">$1')
    .replace(
      /<([^>]+)>([^<]*?)([A-ZÀ-Ÿ_]{2,})([^<]*?)<\/\1>/g,
      (match: any, tag: string, before: any, uppercase: any, after: any) =>
        tag.toLowerCase() === 'img'
          ? match
          : `<${tag}>${before}<span class="highlight-uppercase">${uppercase}</span>${after}</${tag}>`
    )

  // Função para iniciar a leitura em voz alta
  const handleSpeak = () => {
    if (isSpeaking) return
    const utterance = new SpeechSynthesisUtterance()
    utterance.text = fullStyledHtml.replace(/<[^>]+>/g, '') // Remove tags HTML
    utterance.lang = 'pt-BR' // Define a linguagem para português
    utterance.rate = 1 // Velocidade de leitura
    utterance.onend = () => setIsSpeaking(false) // Reseta o estado quando a leitura termina

    setIsSpeaking(true)
    window.speechSynthesis.speak(utterance)
  }
  const handleStop = () => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }

  const domain = getCleanDomain()

  const selectedIcon = getDomainIcon(domain)

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <p className="text-lg font-semibold text-red-600 text-center">Erro ao gerar contrato. Tente novamente.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4 flex flex-col items-center w-full">
      {isLoading && <FullPageSpinner />}
      <div className="flex flex-wrap justify-center gap-2 md:gap-4">
        <Button
          onClick={isSpeaking ? handleStop : handleSpeak}
          className="bg-primary hover:bg-primary-hover text-primary-foreground"
        >
          {isSpeaking ? <VolumeX className="mr-2 h-4 w-4" /> : <Volume2 className="mr-2 h-4 w-4" />}
          {isSpeaking ? 'Parar' : 'Ler em voz alta'}
        </Button>

        <Button
          onClick={() => generatePDF(fullStyledHtml, selectedIcon)}
          className="bg-primary hover:bg-primary-hover text-primary-foreground"
        >
          Baixar PDF
        </Button>
      </div>
      <Card className="w-full max-w-5xl relative min-h-[80vh] overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${selectedIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: '100%', // Ajuste aqui conforme o tamanho ideal da logo
            opacity: 0.1 // Ou use 'opacity-10' do Tailwind
          }}
        />
        <CardHeader className="flex flex-col items-center relative z-10">
          <CardTitle> Contrato</CardTitle>
        </CardHeader>
        <CardContent>
          <div id="pdf-content">
            <section dangerouslySetInnerHTML={{ __html: fullStyledHtml }} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
